'use client';
import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, FileText } from 'lucide-react';
import { InspectionFormData, InspectionCategory, CategoryAnswers } from './inspectionData';

interface Props {
  formData: InspectionFormData;
  categoryAnswers: Record<string, CategoryAnswers>;
  categories: InspectionCategory[];
  getCategoryCompletion: (code: string) => { answered: number; total: number };
}

export default function ReviewSubmitStep({ formData, categoryAnswers, categories, getCategoryCompletion }: Props) {
  const totalItems = categories.reduce((s, c) => s + c.items.length, 0);
  const totalAnswered = categories.reduce((s, c) => {
    const { answered } = getCategoryCompletion(c.code);
    return s + answered;
  }, 0);

  const allFindings = categories.flatMap((cat) =>
    cat.items
      .map((item) => {
        const ans = categoryAnswers[cat.code]?.[item.ref];
        return ans ? { ...ans, ref: item.ref, question: item.question, catCode: cat.code } : null;
      })
      .filter(Boolean)
  );

  const criticalFindings = allFindings.filter((f) => f?.severity === 'Critical' || f?.severity === 'Severe');
  const totalLSD = allFindings.reduce((s, f) => s + (f?.lsdScore || 0), 0);

  return (
    <div className="max-w-4xl space-y-6">
      {/* Summary Banner */}
      <div className="bg-white rounded-xl border p-6 shadow-card" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-2 mb-4">
          <FileText size={18} style={{ color: 'hsl(var(--primary))' }} />
          <h3 className="font-bold text-base">Inspection Summary</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 rounded-xl" style={{ background: 'hsl(var(--secondary))' }}>
            <p className="text-2xl font-bold tabular-nums">{totalAnswered}</p>
            <p className="text-xs text-muted">of {totalItems} items answered</p>
          </div>
          <div className="text-center p-3 rounded-xl" style={{ background: criticalFindings.length > 0 ? 'hsl(355 75% 58% / 0.08)' : 'hsl(var(--secondary))' }}>
            <p className="text-2xl font-bold tabular-nums" style={{ color: criticalFindings.length > 0 ? 'hsl(var(--destructive))' : 'inherit' }}>
              {criticalFindings.length}
            </p>
            <p className="text-xs text-muted">Critical / Severe findings</p>
          </div>
          <div className="text-center p-3 rounded-xl" style={{ background: 'hsl(var(--secondary))' }}>
            <p className="text-2xl font-bold tabular-nums font-mono">{totalLSD}</p>
            <p className="text-xs text-muted">Total LSD Score</p>
          </div>
          <div className="text-center p-3 rounded-xl" style={{ background: 'hsl(var(--secondary))' }}>
            <p className="text-2xl font-bold tabular-nums">{Math.round((totalAnswered / totalItems) * 100)}%</p>
            <p className="text-xs text-muted">Completion rate</p>
          </div>
        </div>
      </div>

      {/* Vessel Details Review */}
      <div className="bg-white rounded-xl border p-5 shadow-card" style={{ borderColor: 'hsl(var(--border))' }}>
        <h3 className="font-semibold text-sm mb-4">Vessel & Inspection Details</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
          {[
            { label: 'Vessel Name', value: formData.vesselName || '—' },
            { label: 'IMO Number', value: formData.imoNumber || '—', mono: true },
            { label: 'Vessel Type', value: formData.vesselType || '—' },
            { label: 'Deadweight', value: formData.deadweight ? `${formData.deadweight} MT` : '—' },
            { label: 'Flag', value: formData.flag || '—' },
            { label: 'Classification', value: formData.classificationSociety || '—' },
            { label: 'Inspector', value: formData.inspectorName || '—' },
            { label: 'Inspection Type', value: formData.inspectionType || '—' },
            { label: 'Date', value: formData.dateOfInspection || '—' },
            { label: 'Port', value: formData.portOfInspection ? `${formData.portOfInspection}, ${formData.countryOfInspection}` : '—' },
            { label: 'Customer Scope', value: formData.customerScope || '—' },
            { label: 'P&I Club', value: formData.piClub || '—' },
          ].map(({ label, value, mono }) => (
            <div key={`review-${label}`}>
              <p className="text-xs text-muted mb-0.5">{label}</p>
              <p className={`text-sm font-semibold ${mono ? 'font-mono' : ''}`}>{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Category Completion */}
      <div className="bg-white rounded-xl border p-5 shadow-card" style={{ borderColor: 'hsl(var(--border))' }}>
        <h3 className="font-semibold text-sm mb-4">Category Completion Status</h3>
        <div className="space-y-2">
          {categories.map((cat) => {
            const { answered, total } = getCategoryCompletion(cat.code);
            const pct = Math.round((answered / total) * 100);
            const isComplete = answered === total;
            return (
              <div key={`review-cat-${cat.code}`} className="flex items-center gap-3">
                <span className="font-mono text-xs w-8 flex-shrink-0" style={{ color: cat.color }}>{cat.code}</span>
                <span className="text-xs flex-1 truncate">{cat.shortLabel}</span>
                <div className="flex items-center gap-2 w-40">
                  <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, background: isComplete ? 'hsl(var(--success))' : cat.color }}
                    />
                  </div>
                  <span className="text-xs tabular-nums text-muted w-12 text-right">{answered}/{total}</span>
                </div>
                {isComplete ? (
                  <CheckCircle2 size={14} style={{ color: 'hsl(var(--success))' }} />
                ) : answered > 0 ? (
                  <AlertTriangle size={14} style={{ color: 'hsl(var(--warning))' }} />
                ) : (
                  <XCircle size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Critical Findings */}
      {criticalFindings.length > 0 && (
        <div className="rounded-xl border p-5" style={{ background: 'hsl(355 75% 58% / 0.04)', borderColor: 'hsl(355 75% 58% / 0.3)' }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} style={{ color: 'hsl(var(--destructive))' }} />
            <h3 className="font-semibold text-sm" style={{ color: 'hsl(var(--destructive))' }}>
              {criticalFindings.length} Critical / Severe Findings — Review Before Submission
            </h3>
          </div>
          <div className="space-y-2">
            {criticalFindings.slice(0, 8).map((f) => f && (
              <div key={`crit-${f.ref}`} className="flex items-start gap-3 text-sm">
                <span className="font-mono text-xs font-semibold flex-shrink-0 mt-0.5" style={{ color: 'hsl(var(--destructive))' }}>{f.ref}</span>
                <span className="flex-1 text-xs">{f.question}</span>
                <span className={`badge ${f.severity === 'Critical' ? 'badge-critical' : 'badge-severe'} flex-shrink-0`}>
                  {f.severity} · LSD {f.lsdScore}
                </span>
              </div>
            ))}
            {criticalFindings.length > 8 && (
              <p className="text-xs text-muted pl-12">+{criticalFindings.length - 8} more findings...</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}